<template>
  <div class="sync-container">

    <!-- Header -->
    <div class="sync-header">
      <div>
        <h2 class="sync-title">Control de Acceso Hikvision</h2>
        <p class="sync-subtitle">Sincroniza personas con el dispositivo biométrico</p>
      </div>
      <div class="header-actions">
        <select v-if="dispositivos.length" v-model="dispositivoId" class="select-device" @change="cambiarDispositivo">
          <option v-for="d in dispositivos" :key="d.id" :value="d.id">{{ d.name }}</option>
        </select>
        <span :class="['device-status', deviceOnline ? 'online' : 'offline']">
          <span class="status-dot"></span>
          {{ deviceOnline ? 'Conectado' : 'Desconectado' }}
        </span>
        <button class="btn-refresh" @click="cargarDatos" :disabled="cargando" title="Recargar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-sm">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </button>
        <router-link to="/panel/dispositivos" class="btn-gestionar" title="Gestionar dispositivos">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-sm">
            <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          Gestionar
        </router-link>
        <button class="btn-upload" @click="iniciarSubida" :disabled="cargando || stats.pendientes === 0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-sm">
            <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
          </svg>
          {{ deviceOnline ? 'Subir' : 'Encolar' }} ({{ stats.pendientes }})
        </button>
        <button class="btn-download" @click="descargarCSV" :disabled="cargando || stats.sincronizados === 0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-sm">
            <polyline points="8 17 12 21 16 17"/><line x1="12" y1="21" x2="12" y2="12"/>
            <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.11"/>
          </svg>
          Descargar ({{ stats.sincronizados }})
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs-row">
      <button :class="['tab-btn', { active: tabActiva === 'miembros' }]" @click="tabActiva = 'miembros'">
        Miembros
      </button>
      <button :class="['tab-btn', { active: tabActiva === 'usuarios' }]" @click="tabActiva = 'usuarios'">
        Usuarios
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-num">{{ stats.total }}</div>
        <div class="stat-label">{{ tabActiva === 'miembros' ? 'Miembros activos' : 'Usuarios' }}</div>
      </div>
      <div class="stat-card success">
        <div class="stat-num">{{ stats.sincronizados }}</div>
        <div class="stat-label">En dispositivo</div>
      </div>
      <div class="stat-card warning">
        <div class="stat-num">{{ stats.pendientes }}</div>
        <div class="stat-label">Pendientes</div>
      </div>
      <div class="stat-card warning">
        <div class="stat-num">{{ stats.enCola }}</div>
        <div class="stat-label">En cola</div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filtros">
      <button
        v-for="f in filtros"
        :key="f.valor"
        :class="['btn-filtro', { active: filtroActivo === f.valor }]"
        @click="filtroActivo = f.valor"
      >{{ f.label }}</button>
      <input v-model="busqueda" type="text" :placeholder="tabActiva === 'miembros' ? 'Buscar miembro...' : 'Buscar usuario...'" class="input-busqueda" />
    </div>

    <!-- Tabla -->
    <div class="tabla-wrapper">
      <div v-if="cargando" class="estado-vacio">
        <v-progress-circular indeterminate color="primary" size="40"/>
        <p>Cargando datos...</p>
      </div>

      <div v-else-if="listaFiltrada.length === 0" class="estado-vacio">
        <p>No hay coincidencias.</p>
      </div>

      <!-- Tabla Miembros -->
      <table v-else-if="tabActiva === 'miembros'" class="sync-table">
        <thead>
          <tr>
            <th>Matrícula</th>
            <th>Nombre</th>
            <th>Cédula / RUC</th>
            <th>Membresía</th>
            <th>Estado</th>
            <th>Dispositivo</th>
            <th class="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in listaFiltrada" :key="m.id_miembro">
            <td><span class="badge-matricula">{{ m.matricula }}</span></td>
            <td class="td-nombre">{{ m.client_nombre }}</td>
            <td>{{ m.client_rucCed }}</td>
            <td>{{ m.nombre_tipo_membresia || '—' }}</td>
            <td><span :class="['badge-estado', estadoClass(m.estado)]">{{ m.estado }}</span></td>
            <td>
              <span v-if="m.en_dispositivo" class="badge-device sync-ok">✓ Sync</span>
              <span v-else-if="m.en_cola" class="badge-device sync-cola">⏳ En cola</span>
              <span v-else class="badge-device sync-no">— No sync</span>
            </td>
            <td class="col-actions">
              <button v-if="!m.en_dispositivo && !m.en_cola" class="btn-agregar" :disabled="m.procesando" @click="agregarAlDispositivo(m)" title="Agregar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </button>
              <button v-else class="btn-quitar" :disabled="m.procesando" @click="quitarDelDispositivo(m)" title="Quitar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </button>
              <span v-if="m.procesando" class="procesando">...</span>
              <span v-if="m.error" class="error-inline" :title="m.error">⚠</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Tabla Usuarios -->
      <table v-else class="sync-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Nombre</th>
            <th>Rol</th>
            <th>Dispositivo</th>
            <th class="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in listaFiltrada" :key="u.id">
            <td><span class="badge-matricula">{{ u.usuario }}</span></td>
            <td class="td-nombre">{{ u.nombre }}</td>
            <td>{{ u.rol || '—' }}</td>
            <td>
              <span v-if="u.en_dispositivo" class="badge-device sync-ok">✓ Sync</span>
              <span v-else-if="u.en_cola" class="badge-device sync-cola">⏳ En cola</span>
              <span v-else class="badge-device sync-no">— No sync</span>
            </td>
            <td class="col-actions">
              <button v-if="!u.en_dispositivo && !u.en_cola" class="btn-agregar" :disabled="u.procesando" @click="agregarAlDispositivo(u)" title="Agregar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </button>
              <button v-else class="btn-quitar" :disabled="u.procesando" @click="quitarDelDispositivo(u)" title="Quitar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="icon-xs">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </button>
              <span v-if="u.procesando" class="procesando">...</span>
              <span v-if="u.error" class="error-inline" :title="u.error">⚠</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Dialog: Resultado de subida bulk -->
    <div v-if="upload.activo" class="dialog-overlay">
      <div class="dialog-box dialog-upload">
        <h3 class="dialog-title">{{ deviceOnline ? 'Sincronizar al dispositivo' : 'Encolar para sincronizar' }}</h3>

        <div v-if="upload.enviando" class="upload-loading">
          <v-progress-circular indeterminate color="primary" size="48"/>
          <p class="upload-loading-txt">Enviando {{ upload.total }} elemento(s)...</p>
        </div>

        <div v-else class="upload-result">
          <div v-if="upload.synced" class="result-row result-ok">
            <span>✓ {{ upload.synced }} sincronizado(s)</span>
          </div>
          <div v-if="upload.queued" class="result-row result-queue">
            <span>⏳ {{ upload.queued }} en cola (dispositivo offline)</span>
          </div>
          <div v-if="upload.errores" class="result-row result-error">
            <span>✗ {{ upload.errores }} con error</span>
          </div>
          <div v-if="!upload.synced && !upload.queued && !upload.errores" class="result-row">
            <span>Sin cambios</span>
          </div>
        </div>

        <div class="dialog-actions">
          <button class="btn-cancelar" @click="upload.activo = false" :disabled="upload.enviando">
            Cerrar
          </button>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import ApiService from '../../Servicios/ApiService'

export default {
  name: 'Sincronizar',

  data: () => ({
    tabActiva:         'miembros',
    miembros:          [],
    usuarios:          [],
    enDispositivo:     [],
    enCola:            [],
    dispositivos:      [],
    dispositivoId:     null,
    deviceOnline:      false,
    cargando:          false,
    busqueda:          '',
    filtroActivo:      'todos',
    upload: {
      activo: false, enviando: false, total: 0,
      synced: 0, queued: 0, errores: 0
    },
    filtros: [
      { valor: 'todos',     label: 'Todos' },
      { valor: 'sync',      label: 'En dispositivo' },
      { valor: 'cola',      label: 'En cola' },
      { valor: 'pendiente', label: 'Pendientes' },
      { valor: 'error',     label: 'Con error' },
    ]
  }),

  computed: {
    miembrosConEstado () {
      return this.miembros.map(m => ({
        ...m,
        en_dispositivo: this.enDispositivo.includes(String(m.id_miembro)),
        en_cola:        this.enCola.includes(String(m.id_miembro)),
        procesando: false,
        error: null
      }))
    },

    usuariosConEstado () {
      return this.usuarios.map(u => ({
        ...u,
        en_dispositivo: this.enDispositivo.includes('U' + String(u.id)),
        en_cola:        this.enCola.includes('U' + String(u.id)),
        procesando: false,
        error: null
      }))
    },

    listaActiva () {
      return this.tabActiva === 'miembros' ? this.miembrosConEstado : this.usuariosConEstado
    },

    listaFiltrada () {
      let lista = this.listaActiva
      if (this.filtroActivo === 'sync')      lista = lista.filter(i => i.en_dispositivo)
      if (this.filtroActivo === 'cola')      lista = lista.filter(i => i.en_cola)
      if (this.filtroActivo === 'pendiente') lista = lista.filter(i => !i.en_dispositivo && !i.en_cola && !i.error)
      if (this.filtroActivo === 'error')     lista = lista.filter(i => i.error)
      if (this.busqueda) {
        const q = this.busqueda.toLowerCase()
        lista = lista.filter(i => {
          const texto = this.tabActiva === 'miembros'
            ? `${i.client_nombre} ${i.client_rucCed} ${i.matricula}`
            : `${i.nombre} ${i.usuario} ${i.rol}`
          return texto.toLowerCase().includes(q)
        })
      }
      return lista
    },

    stats () {
      const lista = this.listaActiva
      return {
        total:         lista.length,
        sincronizados: lista.filter(i => i.en_dispositivo).length,
        enCola:        lista.filter(i => i.en_cola).length,
        pendientes:    lista.filter(i => !i.en_dispositivo && !i.en_cola && !i.error).length,
        errores:       lista.filter(i => i.error).length
      }
    }
  },

  async mounted () {
    await this.cargarDatos()
  },

  methods: {
    async cargarDatos () {
      this.cargando = true
      try {
        const [dataMiembros, dataUsuarios, listaDispositivos] = await Promise.all([
          ApiService.get('api/clientes/miembros/estado/ACTIVO'),
          ApiService.get('api/panel/usuarios'),
          ApiService.centinel.get('/api/devices')
        ])
        this.miembros     = Array.isArray(dataMiembros) ? dataMiembros : (dataMiembros.miembros || [])
        this.usuarios     = Array.isArray(dataUsuarios) ? dataUsuarios : (dataUsuarios.usuarios || [])
        const rawDev      = listaDispositivos.data || listaDispositivos
        this.dispositivos = Array.isArray(rawDev) ? rawDev : []

        if (this.dispositivos.length && !this.dispositivoId) {
          this.dispositivoId = this.dispositivos[0].id
        }

        await this.cargarEstadoDispositivo()
      } catch (err) {
        console.error('Error al cargar datos:', err)
      } finally {
        this.cargando = false
      }
    },

    async cambiarDispositivo () {
      this.enDispositivo = []
      this.deviceOnline  = false
      await this.cargarEstadoDispositivo()
    },

    async cargarEstadoDispositivo () {
      if (!this.dispositivoId) return
      const dispositivo = this.dispositivos.find(d => d.id === this.dispositivoId)
      this.deviceOnline = dispositivo ? dispositivo.status === 'ONLINE' : false
      try {
        const [resEmp, resQueue] = await Promise.all([
          ApiService.centinel.get(`/api/devices/${this.dispositivoId}/employees`),
          ApiService.centinel.get(`/api/sync-queue?deviceId=${this.dispositivoId}&status=PENDING`)
        ])
        const rawEmp         = resEmp.data   || resEmp
        const rawQueue       = resQueue.data || resQueue
        const empleados      = Array.isArray(rawEmp)   ? rawEmp   : []
        const colaPendiente  = Array.isArray(rawQueue) ? rawQueue : []
        this.enDispositivo   = empleados.map(e => String(e.employeeNo))
        this.enCola          = colaPendiente.map(q => String(q.employeeNo))
      } catch (e) {
        this.enDispositivo = []
        this.enCola        = []
      }
    },

    empNo (item) {
      return this.tabActiva === 'usuarios'
        ? 'U' + String(item.id)
        : String(item.id_miembro)
    },

    async agregarAlDispositivo (item) {
      this.$set(item, 'procesando', true)
      this.$set(item, 'error', null)
      const no = this.empNo(item)
      try {
        await ApiService.centinel.post(`/api/devices/${this.dispositivoId}/users`, {
          employeeNo: no,
          name:       item.client_nombre || item.nombre,
          cardNo:     item.client_rucCed || item.usuario || undefined
        })
        this.enDispositivo.push(no)
      } catch (err) {
        this.$set(item, 'error', err.message || 'Error')
      } finally {
        this.$set(item, 'procesando', false)
      }
    },

    async quitarDelDispositivo (item) {
      this.$set(item, 'procesando', true)
      this.$set(item, 'error', null)
      const no = this.empNo(item)
      try {
        await ApiService.centinel.delete(`/api/devices/${this.dispositivoId}/users/${no}`)
        this.enDispositivo = this.enDispositivo.filter(id => id !== no)
      } catch (err) {
        this.$set(item, 'error', err.message || 'Error')
      } finally {
        this.$set(item, 'procesando', false)
      }
    },

    async iniciarSubida () {
      const pendientes = this.listaActiva.filter(i => !i.en_dispositivo && !i.en_cola)
      if (!pendientes.length) return

      this.upload = { activo: true, enviando: true, total: pendientes.length, synced: 0, queued: 0, errores: 0 }

      for (const item of pendientes) {
        const no = this.empNo(item)
        try {
          await ApiService.centinel.post(`/api/devices/${this.dispositivoId}/users`, {
            employeeNo: no,
            name:       item.client_nombre || item.nombre,
            cardNo:     item.client_rucCed || item.usuario || undefined
          })
          this.enDispositivo.push(no)
          this.upload.synced++
        } catch (e) {
          this.upload.errores++
        }
      }

      this.upload.enviando = false
    },

    descargarCSV () {
      const sincronizados = this.listaActiva.filter(i => i.en_dispositivo)
      const esMiembro = this.tabActiva === 'miembros'
      const cabecera = esMiembro
        ? ['Matricula', 'Nombre', 'Cedula/RUC', 'Membresia', 'Estado']
        : ['Usuario', 'Nombre', 'Rol']
      const filas = sincronizados.map(i => esMiembro
        ? [i.matricula || '', i.client_nombre || '', i.client_rucCed || '', i.nombre_tipo_membresia || '', i.estado || '']
        : [i.usuario || '', i.nombre || '', i.rol || '']
      )
      const csv = [cabecera, ...filas]
        .map(f => f.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `dispositivo_usuarios_${new Date().toISOString().slice(0,10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },

    estadoClass (estado) {
      return {
        'ACTIVO':     'estado-activo',
        'VENCIDO':    'estado-vencido',
        'SUSPENDIDO': 'estado-suspendido',
        'CANCELADO':  'estado-cancelado'
      }[estado] || ''
    }
  }
}
</script>

<style scoped>
.sync-container {
  padding: 24px;
  max-width: 1300px;
  margin: 0 auto;
  font-family: var(--font-family);
}

/* Header */
.sync-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.sync-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 4px;
}

.sync-subtitle { color: var(--color-text-secondary); font-size: 0.9rem; margin: 0; }

.header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.select-device {
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  outline: none;
  cursor: pointer;
}

.select-device:focus { border-color: var(--color-primary); }

.device-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
}

.device-status.online  { border-color: var(--color-primary); color: var(--color-primary); }
.device-status.offline { border-color: var(--color-error);   color: var(--color-error); }

.status-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: currentColor;
}

.btn-refresh {
  padding: 8px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: border-color 0.2s;
}

.btn-refresh:hover { border-color: var(--color-primary); }

.btn-sync-all {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: var(--shadow-glow);
  transition: all 0.2s;
}

.btn-sync-all:hover:not(:disabled) { transform: translateY(-1px); }
.btn-sync-all:disabled { opacity: 0.4; cursor: not-allowed; }

/* Tabs */
.tabs-row {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 0;
}

.tab-btn {
  padding: 9px 20px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.2s;
}

.tab-btn.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

.tab-btn:hover:not(.active) { color: var(--color-text-primary); }

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  background: #272f27;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}

.stat-card.success { border-color: rgba(19,236,19,0.3); }
.stat-card.warning { border-color: rgba(245,158,11,0.3); }
.stat-card.error   { border-color: rgba(239,68,68,0.3); }

.stat-num  { font-size: 2rem; font-weight: 700; color: var(--color-text-primary); }
.stat-label { font-size: 0.8rem; color: var(--color-text-muted); margin-top: 2px; }

.stat-card.success .stat-num { color: var(--color-primary); }
.stat-card.warning .stat-num { color: #f59e0b; }
.stat-card.error   .stat-num { color: var(--color-error); }

/* Filtros */
.filtros {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.btn-filtro {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-filtro.active,
.btn-filtro:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.input-busqueda {
  margin-left: auto;
  padding: 7px 14px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  width: 220px;
  outline: none;
}

.input-busqueda:focus { border-color: var(--color-primary); }

/* Tabla */
.tabla-wrapper {
  background: #272f27;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

.sync-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.sync-table th {
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
  font-weight: 600;
  padding: 12px 14px;
  text-align: left;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sync-table td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-primary);
}

.sync-table tbody tr:last-child td { border-bottom: none; }
.sync-table tbody tr:hover { background: var(--color-surface-2); }

.td-nombre { font-weight: 500; }

.badge-matricula {
  background: var(--color-primary-dim);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-estado { padding: 3px 10px; border-radius: 12px; font-size: 0.8rem; font-weight: 600; }
.estado-activo    { background: var(--color-primary-dim); color: var(--color-primary); border: 1px solid var(--color-primary); }
.estado-vencido   { background: rgba(245,158,11,0.12); color: #f59e0b; border: 1px solid rgba(245,158,11,0.3); }
.estado-suspendido{ background: var(--color-surface-2); color: var(--color-text-muted); border: 1px solid var(--color-border); }
.estado-cancelado { background: rgba(239,68,68,0.12); color: var(--color-error); border: 1px solid rgba(239,68,68,0.3); }

.badge-device { padding: 3px 10px; border-radius: 12px; font-size: 0.8rem; font-weight: 600; }
.sync-ok { background: rgba(19,236,19,0.1); color: var(--color-primary); border: 1px solid rgba(19,236,19,0.3); }
.sync-no { background: var(--color-surface-2); color: var(--color-text-muted); border: 1px solid var(--color-border); }

.col-actions { width: 80px; text-align: center; }

.btn-agregar, .btn-quitar {
  padding: 6px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s;
}

.btn-agregar { background: var(--color-primary-dim); color: var(--color-primary); }
.btn-agregar:hover:not(:disabled) { background: var(--color-primary); color: var(--color-bg); }

.btn-quitar { background: rgba(239,68,68,0.12); color: var(--color-error); }
.btn-quitar:hover:not(:disabled) { background: rgba(239,68,68,0.25); }

.btn-agregar:disabled, .btn-quitar:disabled { opacity: 0.3; cursor: not-allowed; }

.procesando { font-size: 0.8rem; color: var(--color-text-muted); margin-left: 4px; }
.error-inline { color: var(--color-warning); margin-left: 4px; cursor: help; }

.icon-sm { width: 16px; height: 16px; }
.icon-xs { width: 15px; height: 15px; }

.estado-vacio {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--color-text-muted);
}

.btn-upload {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--color-primary);
  color: var(--color-bg);
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: var(--shadow-glow);
  transition: all 0.2s;
}
.btn-upload:hover:not(:disabled) { transform: translateY(-1px); }
.btn-upload:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-download {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-download:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
.btn-download:disabled { opacity: 0.4; cursor: not-allowed; }

/* Dialog upload */
.dialog-upload { max-width: 420px; text-align: center; }

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 10px 0 4px;
}

.upload-loading-txt { color: var(--color-text-secondary); font-size: 0.9rem; margin: 0; }

.upload-result {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.result-row {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  background: var(--color-surface-2);
  color: var(--color-text-secondary);
}

.result-ok    { background: rgba(19,236,19,0.1);   color: var(--color-primary); }
.result-queue { background: rgba(245,158,11,0.12); color: #f59e0b; }
.result-error { background: rgba(239,68,68,0.1);   color: var(--color-error); }

.btn-gestionar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 0.2s;
}
.btn-gestionar:hover { border-color: var(--color-primary); color: var(--color-primary); }

/* Dialog upload */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog-box {
  background: #1c2e1c;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
}

.dialog-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  padding: 8px 10px;
  border-radius: 7px;
  border: 1px solid var(--color-border);
  background: var(--color-surface-2);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  outline: none;
}
.form-input:focus { border-color: var(--color-primary); }

.dialog-error {
  color: var(--color-error);
  font-size: 0.85rem;
  margin: 14px 0 0;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
}

.btn-cancelar {
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-cancelar:hover { border-color: var(--color-text-secondary); }

.btn-guardar {
  padding: 8px 22px;
  border-radius: 8px;
  border: none;
  background: var(--color-primary);
  color: var(--color-bg);
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: var(--shadow-glow);
}
.btn-guardar:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 768px) {
  .stats-row { grid-template-columns: 1fr 1fr; }
  .sync-table { font-size: 0.8rem; }
  .input-busqueda { width: 100%; margin-left: 0; }
  .form-grid { grid-template-columns: 1fr; }
}
</style>



// WEBPACK FOOTER //
// src/components/Configuracion/Sincronizar.vue